import { useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router'
import type { TranscriptMessage } from './Chat/selection/clipboard'
import { asFiniteNumber, isRecord } from '~/shared/lib/guards'

/** The projection of a chat message a forwarded-task draft needs. */
type ForwardedMessage = { id: number; text: string | null }

type SelectPayload =
  | { type: 'add' }
  | {
      type: 'createForwardTask'
      message: ForwardedMessage
      ownerGroup: number | null
      timestamp: number
    }
  | { type: 'quoteMessage'; quotedMessages: TranscriptMessage[]; timestamp: number }

/**
 * `selectPayload` arrives as JSON in a URL the operator can edit or copy around, so its shape is
 * checked rather than asserted. A payload that does not match its own `type` reads as absent, which
 * leaves the plain case view rather than building a task or a quote from partial data — previously
 * a hand-edited payload could reach the API as a relation with `object_id: undefined`.
 *
 * Quoted messages are checked for the two fields that reach the API (`object_id`, `content_type`);
 * their remaining fields only feed the rendered blockquote.
 */
const parseSelectPayload = (raw: string): SelectPayload | null => {
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch (_) {
    return null
  }
  if (!isRecord(parsed)) return null

  switch (parsed.type) {
    case 'add':
      return { type: 'add' }
    case 'createForwardTask': {
      const { message } = parsed
      if (!isRecord(message)) return null
      const id = asFiniteNumber(message.id)
      if (id === null) return null
      return {
        type: 'createForwardTask',
        message: { id, text: typeof message.text === 'string' ? message.text : null },
        ownerGroup: asFiniteNumber(parsed.ownerGroup),
        timestamp: asFiniteNumber(parsed.timestamp) ?? 0,
      }
    }
    case 'quoteMessage': {
      const { quotedMessages } = parsed
      if (!Array.isArray(quotedMessages)) return null
      const submittable = quotedMessages.every(
        (message: unknown) =>
          isRecord(message) &&
          typeof message.object_id === 'number' &&
          message.content_type === 'chatmessagev2',
      )
      if (!submittable) return null
      return {
        type: 'quoteMessage',
        quotedMessages: quotedMessages as TranscriptMessage[],
        timestamp: asFiniteNumber(parsed.timestamp) ?? 0,
      }
    }
    default:
      return null
  }
}

/**
 * For use on Patient/Person charts, manage the currently-selected Case
 * and data associated with the selection
 *
 * Uses URL query params as the source of truth for selected items to enable deep-linking
 * Automatically extracts patient/person ID from the current URL path
 */
export const useManageSelectedTodo = () => {
  const navigate = useNavigate()
  const location = useLocation()

  // Extract patient/person ID from URL path
  const patientMatch = location.pathname.match(/\/patients\/(\d+)/)
  const personMatch = location.pathname.match(/\/(?:person|people)\/(\d+)/)

  const patientId = patientMatch?.[1]
  const personId = personMatch?.[1]

  // Parse query params - URL is the source of truth
  const searchParams = new URLSearchParams(location.search)

  const caseId = searchParams.get('caseId')
  const selectedCaseId = caseId ? parseInt(caseId, 10) : null

  const carePlanId = searchParams.get('careplanId')
  const selectedCarePlanId = carePlanId ? parseInt(carePlanId, 10) : null

  const forwardDraftIdParam = searchParams.get('forwardDraftCaseId')
  const forwardDraftCaseId = forwardDraftIdParam ? parseInt(forwardDraftIdParam, 10) : null

  // Extract selectPayload from URL if present
  const payload = searchParams.get('selectPayload')
  const selectPayload = useMemo(() => (payload ? parseSelectPayload(payload) : null), [payload])

  // Shared logic for case navigation with different history methods
  const buildCaseUrl = (caseId: number | null, selectPayload?: SelectPayload) => {
    const baseUrl = patientId ? `/patients/${patientId}/todos` : `/person/${personId}/todos`

    // Create a new URL object to cleanly manage query parameters
    const url = new URL(baseUrl, window.location.origin)

    // Start with current search params to preserve any existing ones
    const searchParams = new URLSearchParams(location.search)

    // Update or remove caseId
    if (caseId !== null) searchParams.set('caseId', caseId.toString())
    else searchParams.delete('caseId')

    // When explicitly navigating to a case, clear forward draft to avoid stale UI
    searchParams.delete('forwardDraftCaseId')

    // Chat-scoped params (managed by useChatURLState) are tied to the case
    // currently being viewed; if the caseId is actually changing, clear them so
    // a stale message ID doesn't 404 against the new case and a stale search
    // doesn't carry over.
    if (caseId !== selectedCaseId) {
      searchParams.delete('scrollToMessageId')
      searchParams.delete('search')
    }

    // Update or remove selectPayload
    if (selectPayload) searchParams.set('selectPayload', JSON.stringify(selectPayload))
    else searchParams.delete('selectPayload')

    // Apply the updated search params
    url.search = searchParams.toString()

    return url.pathname + url.search
  }

  const navigateToCase = (
    caseId: number | null,
    selectPayload?: SelectPayload,
    options?: { preserveChatHighlight?: boolean },
  ) => {
    if (options?.preserveChatHighlight && caseId !== null) {
      const url = buildForwardDraftUrl(caseId, selectPayload)
      void navigate(url)
    } else {
      const url = buildCaseUrl(caseId, selectPayload)
      void navigate(url)
    }
  }

  const resetToCase = (caseId: number | null, selectPayload?: SelectPayload) => {
    const url = buildCaseUrl(caseId, selectPayload)
    void navigate(url, { replace: true })
  }

  // Dedicated builder to set or clear forward-draft on the current page
  const buildForwardDraftUrl = (
    forwardDraftCaseId: number | null,
    selectPayload?: SelectPayload,
  ) => {
    const baseUrl = patientId ? `/patients/${patientId}/todos` : `/person/${personId}/todos`

    const url = new URL(baseUrl, window.location.origin)
    const searchParams = new URLSearchParams(location.search)

    if (forwardDraftCaseId !== null)
      searchParams.set('forwardDraftCaseId', forwardDraftCaseId.toString())
    else searchParams.delete('forwardDraftCaseId')

    if (selectPayload) searchParams.set('selectPayload', JSON.stringify(selectPayload))
    else searchParams.delete('selectPayload')

    url.search = searchParams.toString()
    return url.pathname + url.search
  }

  // Shared logic for care plan navigation with different history methods
  const buildCarePlanUrl = (carePlanId: number | null) => {
    const baseUrl = patientId ? `/patients/${patientId}/todos` : `/person/${personId}/todos`

    // Create a new URL object to cleanly manage query parameters
    const url = new URL(baseUrl, window.location.origin)

    // Start with current search params to preserve any existing ones
    const searchParams = new URLSearchParams(location.search)

    // Update or remove careplanId
    if (carePlanId !== null) {
      searchParams.set('careplanId', carePlanId.toString())
    } else {
      searchParams.delete('careplanId')
    }

    // Apply the updated search params
    url.search = searchParams.toString()

    return url.pathname + url.search
  }

  const navigateToCarePlan = (carePlanId: number | null) => {
    const url = buildCarePlanUrl(carePlanId)
    void navigate(url)
  }

  const resetToCarePlan = (carePlanId: number | null) => {
    const url = buildCarePlanUrl(carePlanId)
    void navigate(url, { replace: true })
  }

  return {
    selectedCaseId,
    selectedCarePlanId,
    forwardDraftCaseId,
    navigateToCase,
    resetToCase,
    navigateToCarePlan,
    resetToCarePlan,
    selectPayload,
  }
}
